import React, {useContext, useState} from "react";
import {Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {AuthLayout} from "../components/AuthLayout";
import {enterToNext} from "../components/enterToNext";
import {AuthPasswordField} from "../components/AuthPasswordField";
import {isPasswordValid} from "../components/PasswordChecklist";
import {PasswordStrengthBar} from "../components/PasswordStrengthBar";
import {a, linkSx, noticeSx, primaryButtonSx} from "../components/authStyles";

export const ChangePasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({password: "", confirmPassword: ""});
  const [loading, setLoading] = useState(false);
  const {onChangePassword} = useContext(SignInContext);
  const navigate = useNavigate();
  const search = useLocation().search;
  const token = new URLSearchParams(search).get("token");

  const validate = () => {
    const nextErrors = {password: "", confirmPassword: ""};
    if (!password) {
      nextErrors.password = "Password is required";
    } else if (!isPasswordValid(password)) {
      nextErrors.password = "Password doesn't meet all the requirements";
    }
    if (!confirmPassword) {
      nextErrors.confirmPassword = "Please confirm your password";
    } else if (password && confirmPassword !== password) {
      nextErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(nextErrors);
    return !nextErrors.password && !nextErrors.confirmPassword;
  };

  const loginHandle = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const toastId = toast.loading("Sending request to change password");
    setLoading(true);
    try {
      await onChangePassword({password, token});
      toast.update(toastId, {
        type: "success",
        render: "Password changed successfully, please check your email address for confirmation",
        isLoading: false,
        autoClose: 2000,
      });
      setPassword("");
      setConfirmPassword("");
      navigate("/login");
    } catch (err) {
      toast.update(toastId, {
        type: "error", render: err?.message || "Please try again later.", isLoading: false, autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      kicker={"New password"}
      title={"Choose a new password"}
      description={"You will use it the next time you sign in."}
      prompt={"Remembered it?"}
      linkText={"Sign in"}
      linkTo={"/login"}
      preserveSearch={false}
    >
      <Stack component={"form"} onSubmit={loginHandle} onKeyDown={enterToNext} direction={"column"} gap={0.5} sx={{width: "100%"}}>
        {!token && (
          <Box sx={{...noticeSx, mb: 1.5}}>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: a.ink}}>This reset link isn't valid</Typography>
            <Typography sx={{fontSize: 13, color: a.muted, mt: 0.25}}>It may be missing or expired.</Typography>
            <Link to={"/forgot-password"} style={{...linkSx, display: "inline-block", marginTop: 6}}>
              Request a new link
            </Link>
          </Box>
        )}
        <AuthPasswordField
          autoFocus
          required
          disabled={!token}
          label={"Password"}
          value={password}
          onChange={e => {
            setPassword(e.target.value);
            setErrors(prev => ({...prev, password: ""}));
          }}
          placeholder={"Enter your new password"}
          error={!!errors.password}
          helperText={errors.password || " "}
        />
        {(password || errors.password) && (
          <PasswordStrengthBar password={password} showErrors={!!errors.password}/>
        )}
        <AuthPasswordField
          required
          disabled={!token}
          label={"Confirm password"}
          value={confirmPassword}
          onChange={e => {
            setConfirmPassword(e.target.value);
            setErrors(prev => ({...prev, confirmPassword: ""}));
          }}
          placeholder={"Re-enter your new password"}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword || " "}
        />

        <Button
          fullWidth
          type={"submit"}
          disabled={loading || !token || !password || !confirmPassword || password !== confirmPassword}
          sx={{...primaryButtonSx, mt: 0.5}}
          startIcon={loading ? <CircularProgress size={16} color={"inherit"}/> : null}
        >
          Save password
        </Button>
      </Stack>
    </AuthLayout>
  );
};
